import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  AppBar,
  Toolbar,
  Pagination,
  Chip,
  useMediaQuery,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Stack,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  Tooltip,
} from "@mui/material";
import {
  Close,
  Article,
  Visibility,
  Person,
  Receipt,
  CheckCircle,
  Cancel,
  ShoppingBag,
  CalendarToday,
  Inventory,
  FilterList,
  Download, // Add this import
  PictureAsPdf, // Add this import
} from "@mui/icons-material";
import { URL as API_URL } from "../constants/url";
import colors from "../constants/colors";

const OrderHistory = ({ adminData, searchText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState({}); // Track downloading state per order
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  
  const ITEMS_PER_PAGE = 10;

  // Fetch orders from API
  // const fetchOrders = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await fetch(`${API_URL}/api/shop/all-users-order-history`);
  //     const data = await res.json();

  //     if (data.success) {
  //       setOrders(data.orders || []);
  //     } else {
  //       showAlert("Error fetching orders", "error");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching orders:", error);
  //     showAlert("Error fetching orders", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };
const fetchOrders = async () => {
  setLoading(true);
  try {
    const res = await fetch(`${API_URL}/api/shop/all-users-order-history`);
    const data = await res.json();

    if (data.success) {
      console.log("Fetched Orders Data:", data.orders);
      // Check first order structure
      if (data.orders.length > 0) {
        console.log("First Order Sample:", data.orders[0]);
        console.log("Has subtotal?", data.orders[0].subtotal);
        console.log("Has deliveryCharge?", data.orders[0].deliveryCharge);
      }
      setOrders(data.orders || []);
    } else {
      showAlert("Error fetching orders", "error");
    }
  } catch (error) {
    console.error("Error fetching orders:", error);
    showAlert("Error fetching orders", "error");
  } finally {
    setLoading(false);
  }
};
  useEffect(() => {
    fetchOrders();
  }, []);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  // Function to handle invoice download
  const handleDownloadInvoice = async (order) => {
    // Set downloading state for this specific order
    setDownloading(prev => ({ ...prev, [order.orderId]: true }));
    
    try {
      // Prepare order data for PDF generation
      const orderData = {
        ...order,
        // Calculate values if not present
        subtotal: order.subtotal || order.totalPrice,
        deliveryCharge: order.deliveryCharge || 0,
        paymentDetails: {
          razorpayPaymentId: order.paymentDetails?.razorpayPaymentId || `pay_${order.orderId?.slice(-8)}`,
          amount: order.paymentDetails?.amount || 
            (order.paymentType === 'advance' ? Math.round(order.totalPrice * 0.1) : order.totalPrice),
          amountRemaining: order.paymentDetails?.amountRemaining || 
            (order.paymentType === 'advance' ? Math.round(order.totalPrice * 0.9) : 0),
          ...order.paymentDetails
        }
      };

      // Call backend to generate PDF
      const response = await fetch(`${API_URL}/api/shop/generate-invoice-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate invoice');
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${order.orderId || Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up URL
      window.URL.revokeObjectURL(url);

      showAlert("Invoice downloaded successfully!", "success");

    } catch (error) {
      console.error('Error downloading invoice:', error);
      showAlert(error.message || "Failed to download invoice. Please try again.", "error");
    } finally {
      // Clear downloading state for this order
      setDownloading(prev => ({ ...prev, [order.orderId]: false }));
    }
  };

  // Filter orders based on search text, payment type and status
  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Apply search filter
    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (order) =>
          order.userName?.toLowerCase().includes(searchLower) ||
          order.orderId?.toLowerCase().includes(searchLower) ||
          order.paymentType?.toLowerCase().includes(searchLower) ||
          order.orderStatus?.toLowerCase().includes(searchLower)
      );
    }

    // Apply payment type filter
    if (paymentTypeFilter) {
      filtered = filtered.filter(
        (order) => order.paymentType === paymentTypeFilter
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(
        (order) => order.orderStatus?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Apply date range filter
    if (startDateFilter || endDateFilter) {
      filtered = filtered.filter((order) => {
        const orderDate = new Date(order.checkoutAt);
        let passes = true;
        
        if (startDateFilter) {
          const startDate = new Date(startDateFilter);
          startDate.setHours(0, 0, 0, 0);
          passes = passes && orderDate >= startDate;
        }
        
        if (endDateFilter) {
          const endDate = new Date(endDateFilter);
          endDate.setHours(23, 59, 59, 999);
          passes = passes && orderDate <= endDate;
        }
        
        return passes;
      });
    }

    return filtered;
  }, [orders, searchText, paymentTypeFilter, statusFilter, startDateFilter, endDateFilter]);

  const paginatedOrders = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredOrders, page]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredOrders.length / ITEMS_PER_PAGE));
    setPage(1);
  }, [filteredOrders]);

  // Filter handlers
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleResetFilters = () => {
    setPaymentTypeFilter("");
    setStatusFilter("");
    setStartDateFilter("");
    setEndDateFilter("");
  };

  const applyFilters = () => {
    handleFilterClose();
  };

  const filterOpen = Boolean(filterAnchorEl);
  const isFilterActive = paymentTypeFilter || statusFilter || startDateFilter || endDateFilter;

  const formatOrderId = (orderId) => {
    if (!orderId) return "#N/A";
    return `#${orderId.slice(-8).toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return colors.green;
      case "partial":
        return colors.orange;
      case "pending":
        return colors.orange;
      case "failed":
        return colors.red;
      default:
        return colors.midGray;
    }
  };

  const getPaymentTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "advance":
        return colors.blue;
      case "full":
        return colors.green;
      default:
        return colors.midGray;
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status?.toLowerCase()) {
      case "paid":
        return "PAID";
      case "partial":
        return "PARTIAL";
      case "pending":
        return "PENDING";
      case "failed":
        return "FAILED";
      default:
        return status?.toUpperCase() || "N/A";
    }
  };

  // const handleViewOrder = (order) => {
  //   setSelectedOrder(order);
  //   setOpenDialog(true);
  // };
  // Inside handleViewOrder function, add:
const handleViewOrder = (order) => {
  console.log("Selected Order Data:", order);
  console.log("Subtotal:", order.subtotal);
  console.log("Delivery Charge:", order.deliveryCharge);
  console.log("Payment Details:", order.paymentDetails);
  console.log("Products:", order.products);
  
  setSelectedOrder(order);
  setOpenDialog(true);
};

  // Generate avatar with green color
  const getOrderAvatar = (order) => {
    const firstChar = order.userName?.charAt(0)?.toUpperCase() || "U";
    return {
      char: firstChar,
      color: colors.green,
    };
  };

  // Custom Alert Component
  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    const isSuccess = severity === "success";
    const borderColor = isSuccess ? colors.limeGreen : colors.deleteBg;
    const iconColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonHoverColor = isSuccess ? colors.green : colors.darkRed;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: { xs: "90vw", sm: 400 },
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${borderColor}`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              backgroundColor: borderColor,
              padding: { xs: "12px 16px", sm: "16px 24px" },
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 28, sm: 36 },
                height: { xs: 28, sm: 36 },
                borderRadius: "50%",
                backgroundColor: "white",
                flexShrink: 0,
              }}
            >
              {isSuccess ? (
                <CheckCircle
                  sx={{ color: iconColor, fontSize: { xs: 18, sm: 22 } }}
                />
              ) : (
                <Cancel
                  sx={{ color: iconColor, fontSize: { xs: 18, sm: 22 } }}
                />
              )}
            </Box>
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isSuccess ? "Success" : "Error"}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
                color: colors.darkBlack,
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              {message}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: buttonColor,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  minWidth: 120,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: buttonHoverColor,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

   return (
    <Box
      sx={{
        height: "100vh", // CHANGE to 100vh
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
        overflow: "hidden", // ADD THIS
      }}
    >
      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* AppBar */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Single Tab for Articles */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              mr: 2,
            }}
          >
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={
                  <Receipt
                    sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }}
                  />
                }
                iconPosition="start"
                label={isMobile ? "Orders" : "Orders History"}
              />
            </Tabs>
          </Box>

          {/* Filter Button */}
          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={handleFilterClick}
            sx={{
              background: colors.darkGreen,
              "&:hover": { background: colors.green },
              fontSize: "0.875rem",
              px: 2.5,
              py: 0.9,
              borderRadius: 3,
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            Filter
          </Button>
        </Toolbar>
      </AppBar>

      {/* Filter Popover */}
      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 1 }}
      >
        <Paper sx={{ p: 3, width: 320 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Filter Orders
            </Typography>
            <IconButton size="small" onClick={handleFilterClose}>
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Payment Type Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                label="Payment Type"
                onChange={(e) => setPaymentTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Payment Types</MenuItem>
                <MenuItem value="full">Full Payment</MenuItem>
                <MenuItem value="advance">Advance Payment</MenuItem>
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select
                value={statusFilter}
                label="Order Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            {/* Date Range Filter */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Date Range
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    From Date
                  </Typography>
                  <input
                    type="date"
                    value={startDateFilter}
                    onChange={(e) => setStartDateFilter(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      fontSize: "0.875rem",
                      fontFamily: "inherit",
                      marginTop: "4px",
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    To Date
                  </Typography>
                  <input
                    type="date"
                    value={endDateFilter}
                    onChange={(e) => setEndDateFilter(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      border: "1px solid rgba(0, 0, 0, 0.23)",
                      borderRadius: "4px",
                      fontSize: "0.875rem",
                      fontFamily: "inherit",
                      marginTop: "4px",
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button
                onClick={handleResetFilters}
                variant="outlined"
                fullWidth
                sx={{
                  borderColor: colors.green,
                  color: colors.green,
                  fontWeight: 600,
                }}
              >
                Reset
              </Button>
              <Button
                onClick={applyFilters}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: colors.green,
                  fontWeight: 600,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popover>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: { xs: 2, sm: 3 },
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {/* Filter Status */}
        {isFilterActive && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {paymentTypeFilter && (
              <Chip
                label={`Payment: ${paymentTypeFilter === 'full' ? 'Full Payment' : 'Advance Payment'}`}
                size="small"
                onDelete={() => setPaymentTypeFilter('')}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: colors.lightGreen,
                  color: colors.darkGreen,
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: colors.darkGreen,
                    '&:hover': {
                      color: colors.green,
                    }
                  }
                }}
              />
            )}
            {statusFilter && (
              <Chip
                label={`Status: ${getStatusDisplayText(statusFilter)}`}
                size="small"
                onDelete={() => setStatusFilter('')}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: getStatusColor(statusFilter) + '20',
                  color: getStatusColor(statusFilter),
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: getStatusColor(statusFilter),
                    '&:hover': {
                      opacity: 0.8,
                    }
                  }
                }}
              />
            )}
            {startDateFilter && (
              <Chip
                label={`From: ${new Date(startDateFilter).toLocaleDateString()}`}
                size="small"
                onDelete={() => setStartDateFilter('')}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: colors.lightGreen,
                  color: colors.darkGreen,
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: colors.darkGreen,
                    '&:hover': {
                      color: colors.green,
                    }
                  }
                }}
              />
            )}
            {endDateFilter && (
              <Chip
                label={`To: ${new Date(endDateFilter).toLocaleDateString()}`}
                size="small"
                onDelete={() => setEndDateFilter('')}
                deleteIcon={<Close />}
                sx={{
                  backgroundColor: colors.lightGreen,
                  color: colors.darkGreen,
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: colors.darkGreen,
                    '&:hover': {
                      color: colors.green,
                    }
                  }
                }}
              />
            )}
            <Button
              size="small"
              onClick={handleResetFilters}
              sx={{
                color: colors.green,
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'none'
              }}
            >
              Clear All
            </Button>
          </Box>
        )}

        {/* Search Results Info */}
        {searchText && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: colors.lightGray,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              Search results for: <strong>"{searchText}"</strong>
              {filteredOrders.length > 0 &&
                ` (${filteredOrders.length} order${
                  filteredOrders.length > 1 ? "s" : ""
                } found)`}
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress
                size={60}
                sx={{
                  color: colors.green,
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                Loading orders...
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {/* Orders List */}
            <Box sx={{ width: "100%" }}>
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => {
                  const avatar = getOrderAvatar(order);
                  const isDownloading = downloading[order.orderId] || false;
                  
                  return (
                    <Card
                      key={order.orderId}
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${colors.lightGray}`,
                        mb: 2,
                        p: 0,
                        transition: "0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                          borderColor: colors.green,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2, sm: 2.5 },
                          "&:last-child": { pb: 2 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 2.5,
                          }}
                        >
                          {/* LEFT SECTION - Avatar and Basic Info */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              flex: 1,
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: avatar.color,
                                width: 55,
                                height: 55,
                                fontWeight: "bold",
                                fontSize: "1.1rem",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              }}
                            >
                              {avatar.char}
                            </Avatar>

                            <Box>
                              {/* Order ID */}
                              <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                color={colors.orange}
                                sx={{
                                  letterSpacing: 0.5,
                                  fontSize: "1rem",
                                  mb: 0.5,
                                }}
                              >
                                {formatOrderId(order.orderId)}
                              </Typography>

                              {/* Username */}
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                color={colors.darkBlack}
                                sx={{ mb: 0.4 }}
                              >
                                {order.userName}
                              </Typography>

                              {/* Date */}
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "0.82rem",
                                }}
                              >
                                <CalendarToday sx={{ fontSize: 14, mr: 0.5 }} />
                                {formatDate(order.checkoutAt)}
                              </Typography>
                            </Box>
                          </Box>

                          {/* RIGHT SECTION - Order Summary */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 2,
                              flexWrap: "wrap",
                              textAlign: "center",
                            }}
                          >
                            {/* STATUS */}
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontWeight: 500 }}
                              >
                                STATUS
                              </Typography>
                              <Chip
                                label={getStatusDisplayText(order.orderStatus)}
                                size="small"
                                sx={{
                                  mt: 0.5,
                                  backgroundColor: getStatusColor(
                                    order.orderStatus
                                  ),
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  height: 26,
                                  px: 1.2,
                                }}
                              />
                            </Box>

                            {/* PAYMENT TYPE */}
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontWeight: 500 }}
                              >
                                PAYMENT
                              </Typography>
                              <Chip
                                label={
                                  order.paymentType === "advance"
                                    ? "Advance"
                                    : "Full Payment"
                                }
                                size="small"
                                variant="outlined"
                                sx={{
                                  mt: 0.5,
                                  borderColor: getPaymentTypeColor(
                                    order.paymentType
                                  ),
                                  color: getPaymentTypeColor(order.paymentType),
                                  fontWeight: 600,
                                  fontSize: "0.75rem",
                                  height: 26,
                                  px: 1.2,
                                }}
                              />
                            </Box>

                            {/* PRICE */}
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", fontWeight: 500 }}
                              >
                                TOTAL
                              </Typography>
                              <Typography
                                variant="h6"
                                fontWeight={700}
                                color={colors.darkGreen}
                                sx={{ mt: 0.3, fontSize: "1.15rem" }}
                              >
                                {formatPrice(order.totalPrice)}
                              </Typography>
                            </Box>

                            {/* DOWNLOAD INVOICE BUTTON */}
                       {/* DOWNLOAD INVOICE BUTTON - Changed from PDF icon to button */}
<Tooltip title="Download Invoice">
  <Button
    onClick={() => handleDownloadInvoice(order)}
    disabled={isDownloading}
    variant="outlined"
    size="small"
    startIcon={isDownloading ? <CircularProgress size={16} /> : <Download />}
    sx={{
      borderColor: colors.blue,
      color: colors.blue,
      '&:hover': {
        backgroundColor: colors.blue,
        color: 'white',
        borderColor: colors.blue,
      },
      '&:disabled': {
        opacity: 0.5,
        borderColor: colors.midGray,
        color: colors.midGray,
      },
     fontSize: "0.8rem",
                                borderRadius: 2,
                                //boxShadow: "0 3px 10px rgba(76, 175, 80, 0.25)",
                                px: 2.5,
                                py: 0.9,
                                minWidth: 130,
    }}
  >
    {isDownloading ? 'Downloading...' : 'Invoice'}
  </Button>
</Tooltip>

                            {/* VIEW BUTTON */}
                            <Button
                              variant="contained"
                              startIcon={<Visibility />}
                              onClick={() => handleViewOrder(order)}
                              sx={{
                                background: `linear-gradient(135deg, ${colors.green}, ${colors.darkGreen})`,
                                "&:hover": {
                                  background: `linear-gradient(135deg, ${colors.darkGreen}, ${colors.green})`,
                                  transform: "translateY(-1px)",
                                },
                                fontSize: "0.8rem",
                                borderRadius: 2,
                                boxShadow: "0 3px 10px rgba(76, 175, 80, 0.25)",
                                px: 2.5,
                                py: 0.9,
                                minWidth: 130,
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Receipt
                    sx={{
                      fontSize: { xs: 64, sm: 80 },
                      color: colors.midGray,
                      mb: 2,
                      opacity: 0.5,
                    }}
                  />
                  <Typography
                    variant="h5"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" }, mb: 1 }}
                  >
                    {searchText || isFilterActive
                      ? "No orders match your criteria"
                      : "No orders found"}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ opacity: 0.7 }}
                  >
                    {searchText || isFilterActive
                      ? "Try adjusting your search terms or filters"
                      : "Orders will appear here once they are placed"}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 4,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: colors.darkBlack,
                      fontSize: { xs: "0.9rem", sm: "1.1rem" },
                      fontWeight: 500,
                      minWidth: { xs: "32px", sm: "42px" },
                      height: { xs: "32px", sm: "42px" },
                      margin: "0 2px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: colors.lightGreen,
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                      },
                    },
                    "& .MuiPaginationItem-page.Mui-selected": {
                      backgroundColor: colors.darkGreen,
                      color: colors.white,
                      fontWeight: 600,
                      border: `2px solid ${colors.darkGreen}`,
                      boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Order Details Dialog - Section-wise Layout */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            maxHeight: "90vh",
          },
        }}
      >
        {selectedOrder && (
          <>
            {/* Header */}
            <DialogTitle
              sx={{
                backgroundColor: colors.darkGreen,
                color: "white",
                py: 1.5,
                px: 2.5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1">Order Details</Typography>
                  <Typography
                    variant="h7"
                    sx={{ color: colors.white, fontWeight: "bold" }}
                  >
                    {formatOrderId(selectedOrder.orderId)}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setOpenDialog(false)}
                  size="small"
                  sx={{ color: "white" }}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              {/* Billing Details */}
              <Box sx={{ p: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    color: colors.darkGreen,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "bold",
                  }}
                >
                  <Person sx={{ fontSize: 20 }} />
                  Billing Customer Details
                </Typography>

                <Stack spacing={1.5}>
                  <Typography variant="body2">
                    Name: {selectedOrder.billingCustomerDetails?.name || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Phone:{" "}
                    {selectedOrder.billingCustomerDetails?.phone || "N/A"}
                  </Typography>

                  <Typography variant="body2">
                    Address:{" "}
                    {selectedOrder.billingCustomerDetails?.address ||
                      "No address provided"}
                  </Typography>
                </Stack>
              </Box>

              <Divider />

              {/* Shipping Details */}
              <Box sx={{ p: 2.5 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    color: colors.darkGreen,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "bold",
                  }}
                >
                  <ShoppingBag sx={{ fontSize: 20 }} />
                  Shipping Customer Details
                </Typography>

                {selectedOrder.shippingCustomerDetails ? (
                  <Stack spacing={1.5}>
                    <Typography variant="body2">
                      Name: {selectedOrder.shippingCustomerDetails.name}
                    </Typography>
                    <Typography variant="body2">
                      Phone: {selectedOrder.shippingCustomerDetails.phone}
                    </Typography>

                    <Typography variant="body2">
                      Address:{" "}
                      {selectedOrder.shippingCustomerDetails.deliveryAddress}
                    </Typography>
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      backgroundColor: colors.lightGray,
                      p: 2,
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Same as billing address
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider />
              {/* Order Information */}
            {/* Order Information */}
{/* Order Information */}
<Box sx={{ p: 2.5 }}>
  <Typography
    variant="subtitle1"
    sx={{
      mb: 2,
      color: colors.darkGreen,
      display: "flex",
      alignItems: "center",
      gap: 1,
      fontWeight: "bold",
    }}
  >
    <Receipt sx={{ fontSize: 20 }} />
    Order Information
  </Typography>

  <Stack spacing={2}>
    {/* Order ID & Date */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="body2" color="text.secondary">
          Order ID
        </Typography>
        <Typography variant="body2" color={colors.orange}>
          {formatOrderId(selectedOrder.orderId)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="body2" color="text.secondary">
          Order Date & Time
        </Typography>
        <Typography variant="body2">
          {formatDate(selectedOrder.checkoutAt)}
        </Typography>
      </Box>
    </Box>

    {/* Status & Payment */}
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="body2" color="text.secondary">
          Order Status
        </Typography>
        <Chip
          label={getStatusDisplayText(selectedOrder.orderStatus)}
          size="small"
          sx={{
            backgroundColor: getStatusColor(selectedOrder.orderStatus),
            color: "white",
            fontSize: "0.75rem",
            mt: 0.5,
          }}
        />
      </Box>
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="body2" color="text.secondary">
          Payment Type
        </Typography>
        <Chip
          label={selectedOrder.paymentType === "advance" ? "Advance" : "Full"}
          size="small"
          variant="outlined"
          sx={{
            borderColor: getPaymentTypeColor(selectedOrder.paymentType),
            color: getPaymentTypeColor(selectedOrder.paymentType),
            fontSize: "0.75rem",
            mt: 0.5,
          }}
        />
      </Box>
    </Box>

    {/* Payment Details - Only show if exists */}
    {selectedOrder.paymentDetails && Object.keys(selectedOrder.paymentDetails).length > 0 && (
      <Box
        sx={{
          backgroundColor: colors.lightGray,
          p: 1.5,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, color: colors.darkGreen, fontWeight: "bold" }}
        >
          Payment Details
        </Typography>
        <Stack spacing={0.5}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Payment ID:
            </Typography>
            <Typography variant="caption" fontWeight="500">
              {selectedOrder.paymentDetails?.razorpayPaymentId || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="caption" color="text.secondary">
              Amount Paid:
            </Typography>
            <Typography variant="caption" fontWeight="500">
              {formatPrice(selectedOrder.paymentDetails?.amount || 0)}
            </Typography>
          </Box>
          {selectedOrder.paymentType === "advance" && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption" color="text.secondary">
                Amount Remaining:
              </Typography>
              <Typography variant="caption" fontWeight="500">
                {formatPrice(selectedOrder.paymentDetails?.amountRemaining || 0)}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>
    )}

    {/* Price Breakdown - Fixed with fallback values */}
    <Box
      sx={{
        border: `1px solid ${colors.lightGray}`,
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 2, color: colors.darkGreen, fontWeight: "bold" }}
      >
        Price Breakdown
      </Typography>
      
      <Stack spacing={1}>
        {/* Subtotal - Calculate if not present */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal
          </Typography>
          <Typography variant="body2">
            {formatPrice(
              selectedOrder.subtotal || 
              (selectedOrder.products?.reduce((sum, product) => {
                const price = product.priceAtPurchase || product.offerPrice || product.price || 0;
                const quantity = product.quantity || 1;
                return sum + (price * quantity);
              }, 0) || 0)
            )}
          </Typography>
        </Box>

        {/* Delivery Charge */}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" color="text.secondary">
            Delivery Charge
          </Typography>
          <Typography variant="body2">
            {formatPrice(selectedOrder.deliveryCharge || 0)}
          </Typography>
        </Box>

        {/* Divider */}
        <Divider sx={{ my: 1 }} />

        {/* Total Amount */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: colors.lightGreen,
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${colors.green}`,
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: colors.darkGreen, fontWeight: "bold" }}
          >
            Total Amount
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: colors.darkGreen, fontWeight: "bold" }}
          >
            {formatPrice(selectedOrder.totalPrice || 0)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </Stack>
</Box>

              <Divider />

              {/* Order Items */}
           {/* Order Items */}
{/* Order Items */}
<Box sx={{ p: 2.5 }}>
  <Typography
    variant="subtitle1"
    sx={{
      mb: 1,
      color: colors.darkGreen,
      display: "flex",
      alignItems: "center",
      gap: 1,
      fontWeight: "bold",
    }}
  >
    <Inventory sx={{ fontSize: 20 }} />
    Order Items ({selectedOrder.products?.length || 0})
  </Typography>

  <List sx={{ py: 0, mt: 1 }}>
    {selectedOrder.products?.map((product, index) => {
      // Calculate product total - with fallbacks
      const productPrice = product.priceAtPurchase || product.offerPrice || product.price || 0;
      const productQuantity = product.quantity || 1;
      const productTotal = productPrice * productQuantity;
      
      return (
        <ListItem
          key={index}
          sx={{
            px: 0,
            py: 2,
            borderBottom:
              index < selectedOrder.products.length - 1
                ? `1px solid ${colors.lightGray}`
                : "none",
          }}
        >
          <ListItemAvatar>
            {product.images?.length > 0 ? (
              <Avatar
                src={`${API_URL}${product.images[0]}`}
                variant="rounded"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: 2,
                  border: `1px solid ${colors.lightGray}`,
                }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: 2,
                  border: `1px solid ${colors.lightGray}`,
                  backgroundColor: colors.lightGray,
                }}
              >
                <Article sx={{ color: colors.midGray }} />
              </Avatar>
            )}
          </ListItemAvatar>

          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  color: colors.darkBlack,
                  mb: 0.5,
                  ml: 1.8,
                  fontWeight: "bold",
                }}
              >
                {product.title || "Unknown Product"}
              </Typography>
            }
            secondary={
              <Stack spacing={0.5} sx={{ ml: 1.8 }}>
                <Typography variant="caption" color="text.secondary">
                  Quantity: {productQuantity}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Price: {formatPrice(productPrice)}
                </Typography>
                {(product.price > 0 && product.offerPrice > 0 && product.offerPrice < product.price) && (
                  <Typography 
                    variant="caption" 
                    color={colors.orange} 
                    sx={{ 
                      fontSize: '0.7rem',
                      backgroundColor: colors.lightOrange,
                      px: 0.5,
                      py: 0.1,
                      borderRadius: 0.5
                    }}
                  >
                    You saved: {formatPrice(product.price - productPrice)}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color={colors.darkGreen}
                  sx={{ fontWeight: "bold", mt: 0.5 }}
                >
                  Total: {formatPrice(productTotal)}
                </Typography>
              </Stack>
            }
          />
        </ListItem>
      );
    })}
  </List>
</Box>

              <Divider />
            </DialogContent>

            {/* Footer with Download Button */}
            <DialogActions
              sx={{ 
                p: 1.5, 
                borderTop: `1px solid ${colors.lightGray}`,
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <Button
                onClick={() => handleDownloadInvoice(selectedOrder)}
                variant="outlined"
                startIcon={<Download />}
                disabled={downloading[selectedOrder?.orderId] || false}
                sx={{
                  borderColor: colors.blue,
                  color: colors.blue,
                  borderRadius: 1.5,
                  px: 3,
                  py: 0.8,
                  fontSize: "0.9rem",
                  textTransform: "none",
                  '&:hover': {
                    backgroundColor: colors.blue + '10',
                    borderColor: colors.blue,
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  }
                }}
              >
                {downloading[selectedOrder?.orderId] ? 'Generating PDF...' : 'Download Invoice'}
              </Button>
              
              <Button
                onClick={() => setOpenDialog(false)}
                variant="contained"
                sx={{
                  backgroundColor: colors.darkGreen,
                  "&:hover": { backgroundColor: colors.green },
                  borderRadius: 1.5,
                  px: 3,
                  py: 0.8,
                  fontSize: "0.9rem",
                  textTransform: "none",
                }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default OrderHistory;